// ---------------------------------------------------------------------------
// useFileSource — bundles the file-mode UI state for one ItemsSourcePicker
// (parsed items + file name + parse error + the handler). The form needs the
// resulting `items` for submit + validation, so the hook lives at the form
// level (not inside the picker) — but bundling here saves the form from
// declaring the same state bundle + handler for each query/data set.
// ---------------------------------------------------------------------------

import { useCallback, useState } from 'react';

import type { InlineItemsAnalysis } from '../EvaluationTemplateForm.utils.js';
import { readFileAsInlineItemsAnalysis } from '../EvaluationTemplateForm.utils.js';

export interface FileSource {
  items: string[] | null;
  analysis: InlineItemsAnalysis | null;
  name: string | null;
  error: string | null;
  handle: (file: File) => Promise<void>;
}

export function useFileSource(): FileSource {
  const [items, setItems] = useState<string[] | null>(null);
  const [analysis, setAnalysis] = useState<InlineItemsAnalysis | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handle = useCallback(async (file: File) => {
    setError(null);
    setName(file.name);
    try {
      const nextAnalysis = await readFileAsInlineItemsAnalysis(file);
      if (nextAnalysis.items === null) {
        setItems(null);
        setAnalysis(nextAnalysis);
        setError(
          `Couldn't parse '${file.name}'. ${
            nextAnalysis.error ??
            'Provide a JSON array or JSONL (one JSON object per line).'
          }`,
        );
        return;
      }
      setItems(nextAnalysis.items);
      setAnalysis(nextAnalysis);
    } catch (err) {
      setItems(null);
      setAnalysis(null);
      setError(err instanceof Error ? err.message : 'Failed to read file.');
    }
  }, []);

  return { items, analysis, name, error, handle };
}
